import { gql } from '@apollo/client';
import React, { useReducer, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import isTouchDevice from 'is-touch-device';
import loadable from '@loadable/component';
import { VariantCellFragment } from '../../../../generated/types/VariantCellFragment';
import { VERTICALS, Vertical } from '../../../../constants';
import { hrefFor } from '../../../../routers';
import { tc } from '../../../../lib/trackingContext';
import { getActiveVariantBy } from '../../../../lib/getActiveVariantBy';
import {
  useHasAccessToProductOrTrunkshow,
  HAS_ACCESS_TO_PRODUCT_FRAGMENT
} from '../../../../hooks/useHasAccessToProductOrTrunkshow';
import { FAVORITE_FRAGMENT } from '../../../Favorite';
import { Link } from '../../../Link';
import { LazyLoad } from '../../../LazyLoad';
import {
  VariantCellImageGallery,
  VARIANT_CELL_IMAGE_GALLERY_FRAGMENT
} from './VariantCellImageGallery';
import { VariantCellDetails, VARIANT_CELL_DETAILS_FRAGMENT } from './VariantCellDetails';

import './VariantCell.scss';

const Favorite = loadable(() => import('../../../Favorite'), {
  resolveComponent: exports => exports.Favorite
});

export const VARIANT_CELL_FRAGMENT = gql`
  fragment VariantCellFragment on Variant {
    id
    gender
    productId: product_id
    designerSlug: designer_slug
    productSlug: slug
    masterVariant: master_variants_data {
      id
    }
    otherVariants: other_variants_data {
      id
    }
    objectid
    index
    ...HasAccessToProductFragment
    ...VariantCellImageGalleryFragment
    ...VariantCellDetailsFragment
    ...FavoriteFragment
  }
  ${HAS_ACCESS_TO_PRODUCT_FRAGMENT}
  ${VARIANT_CELL_IMAGE_GALLERY_FRAGMENT}
  ${VARIANT_CELL_DETAILS_FRAGMENT}
  ${FAVORITE_FRAGMENT}
`;

enum Mode {
  Resting,
  Active
}

interface State {
  mode: Mode;
  activeVariantId: string;
}

type Action =
  | { type: 'SET_ACTIVE_VARIANT_ID'; payload: { id: string } }
  | { type: 'SET_MODE'; payload: { mode: Mode } };

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  variant: VariantCellFragment;
  removeUnfilteredSizes?: boolean;
  quickAddToBag?: boolean;
  moduleName?: string | null;
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload.mode };
    case 'SET_ACTIVE_VARIANT_ID':
      return { ...state, activeVariantId: action.payload.id };
    default:
      return state;
  }
};

export const VariantCell: React.FC<Props> = React.memo(
  ({ className, variant, moduleName, ...rest }) => {
    const { hasAccess } = useHasAccessToProductOrTrunkshow(variant);

    const [state, dispatch] = useReducer(reducer, {
      mode: Mode.Resting,
      activeVariantId: variant.masterVariant.id
    });

    const vertical = VERTICALS[variant.gender as Vertical];

    const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', state.activeVariantId), [
      variant,
      state.activeVariantId
    ]);

    const handleSelectColor = useCallback(
      (color: string) => {
        const { id } = getActiveVariantBy(variant, 'color', color);

        dispatch({ type: 'SET_ACTIVE_VARIANT_ID', payload: { id } });
      },
      [variant]
    );

    const handleMouseEnter = useCallback(() => {
      if (isTouchDevice()) return;

      dispatch({ type: 'SET_MODE', payload: { mode: Mode.Active } });
    }, []);

    const handleMouseLeave = useCallback(() => {
      if (isTouchDevice()) return;

      dispatch({ type: 'SET_MODE', payload: { mode: Mode.Resting } });
    }, []);

    const trackHrefClick = useCallback(() => {
      tc.track('Product Clicked', { addToScopeOnce: { variant, activeVariant, moduleName } });
    }, [variant, activeVariant, moduleName]);

    const href = hrefFor.ProductDetailPage({
      vertical,
      designerSlug: variant.designerSlug || '',
      productSlug: variant.productSlug,
      variantId: activeVariant.id
    });

    if (!hasAccess) return null;

    return (
      <div
        className={classnames('VariantCell', className, {
          'VariantCell--active': state.mode === Mode.Active
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-variant-pk={variant.id}
        data-product-id={variant.productId}
        {...rest}
      >
        <div className="VariantCell__wrapper">
          <div className="VariantCell__content">
            <Link
              className="VariantCell__image"
              to={href}
              rel="nofollow noindex"
              onClick={trackHrefClick}
              aria-label={variant.name || ''}
            >
              <VariantCellImageGallery
                key={activeVariant.id}
                variant={variant}
                activeVariantId={activeVariant.id}
                active={state.mode !== Mode.Resting}
              />
            </Link>
          </div>

          <LazyLoad>
            <Favorite type="ICON" variant={variant} />
          </LazyLoad>

          <VariantCellDetails
            variant={variant}
            activeVariantId={activeVariant.id}
            href={href}
            active={state.mode === Mode.Active}
            trackHrefClick={trackHrefClick}
            onSelectColor={handleSelectColor}
          />
        </div>
      </div>
    );
  }
);
